import { useEffect, useRef, useState } from 'react';
import { ExternalLink, Pencil } from 'lucide-react';
import { Badge } from '@/components/ui/badge.jsx';
import AircraftIdentityDialog from '@/components/account/AircraftIdentityDialog.jsx';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet.jsx';
import { usePeekStore } from '@/stores/peek-store.js';
import { openContactPeek, dealUrl } from '@/lib/links.js';
import { formatCurrency, formatHumanDate } from '@/lib/format.js';
import { api } from '@/lib/api.js';
import useMediaQuery from '@/hooks/useMediaQuery.js';
import { aircraftLabel } from '@/lib/aircraftLabel.js';

export default function AircraftPeekSheet({ id, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  // Bumped after an identity save so the sheet re-reads the store it just
  // wrote to, instead of showing the value it replaced.
  const [reload, setReload] = useState(0);
  const aborted = useRef(false);
  const openPeek = usePeekStore((s) => s.openPeek);
  const isDesktop = useMediaQuery('(min-width: 768px)');

  useEffect(() => {
    if (!id) return;
    aborted.current = false;
    setLoading(true);
    setError(null);
    api
      .get(`/api/tracon/aircraft/${encodeURIComponent(id)}/peek`)
      .then((r) => {
        if (!aborted.current) setData(r);
      })
      .catch((err) => {
        if (!aborted.current) setError(err);
      })
      .finally(() => {
        if (!aborted.current) setLoading(false);
      });
    return () => {
      aborted.current = true;
    };
  }, [id, reload]);

  const ident = data?.identity || null;
  const specs = [
    ['Seats', data?.seats ?? ident?.seats, false],
    ['Gear', ident?.undercarriage_type, false],
    ['Storage', data?.storage, false],
    ['Airport', data?.airport_identifier, true],
    ['Engine', ident?.engine_type, false],
    ['Class', ident?.aircraft_type, false],
    ['Serial', ident?.serial_number, true],
    ['AMIN', data?.amin_code, true],
  ].filter(([, v]) => v != null && v !== '');

  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side={isDesktop ? 'right' : 'bottom'}
        className="flex flex-col overflow-hidden p-0 sm:max-w-lg"
      >
        <div className="p-6 pb-0">
          <SheetHeader>
            <SheetTitle className="font-mono text-lg">
              {data?.tail_number || 'Aircraft'}
            </SheetTitle>
            <SheetDescription>
              {aircraftLabel(data)}
            </SheetDescription>
          </SheetHeader>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {loading && !data ? (
            <p className="text-sm text-text-muted">Loading…</p>
          ) : error ? (
            <p className="text-sm text-accent">
              Failed to load aircraft: {error.message || 'unknown'}
            </p>
          ) : data ? (
            <>
              {/* Specs */}
              <div>
                <h4 className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-text-dim">
                  Specs
                </h4>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  {specs.map(([label, value, mono]) => (
                    <div key={label}>
                      <span className="text-text-dim">{label}</span>{' '}
                      <span className={mono ? 'font-mono text-text' : 'text-text'}>{value}</span>
                    </div>
                  ))}
                  {ident && !ident.undercarriage_type ? (
                    <div>
                      <span className="text-text-dim">Gear</span>{' '}
                      <span className="italic text-text-dim">not stated</span>
                    </div>
                  ) : null}
                </div>
                {data.tail_number ? (
                  <button
                    type="button"
                    onClick={() => setEditing(true)}
                    className="mt-2 inline-flex items-center gap-1 rounded-[5px] border border-border bg-surface px-2 py-1 text-[11px] text-text-muted hover:border-border-strong hover:bg-surface-strong hover:text-text"
                  >
                    <Pencil size={12} strokeWidth={1.5} />
                    Edit identity
                  </button>
                ) : null}
              </div>

              {/* FAA Registration */}
              <div>
                <h4 className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-text-dim">
                  FAA Registration
                </h4>
                {data.faa_registration ? (
                  <div className="space-y-0.5 text-xs">
                    {data.faa_registration.owner ? (
                      <div>
                        <span className="text-text-dim">Owner</span>{' '}
                        <span className="text-text">
                          {data.faa_registration.owner}
                        </span>
                      </div>
                    ) : null}
                    {data.faa_registration.owner_dispute?.status === 'disputed' ? (
                      <p className="text-warning">
                        Typed on a client&apos;s intake as {data.faa_registration.owner_dispute.stated_name}. The FAA now shows a different owner. Check which is right.
                      </p>
                    ) : data.faa_registration.owner_dispute?.status === 'unknown' ? (
                      <p className="text-text-dim">
                        Could not check the typed owner against the FAA just now.
                      </p>
                    ) : null}
                    {data.faa_registration.status ? (
                      <div>
                        <span className="text-text-dim">Status</span>{' '}
                        <span className="text-text">
                          {data.faa_registration.status}
                        </span>
                      </div>
                    ) : null}
                    {data.faa_registration.last_action_date ? (
                      <div>
                        <span className="text-text-dim">Last action</span>{' '}
                        <span className="text-text">
                          {formatHumanDate(data.faa_registration.last_action_date) || data.faa_registration.last_action_date}
                        </span>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <p className="text-xs italic text-text-dim">
                    No FAA registration found
                  </p>
                )}
              </div>

              {/* On policies */}
              {data.on_policies?.length > 0 ? (
                <div>
                  <h4 className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-text-dim">
                    On policies
                  </h4>
                  <ul className="space-y-1.5">
                    {data.on_policies.map((d) => {
                      const faaOwner = (data.faa_registration?.owner || '').toLowerCase().trim();
                      const acctName = (d.account_name || '').toLowerCase().trim();
                      const showName = acctName && faaOwner && !acctName.includes(faaOwner) && !faaOwner.includes(acctName);
                      return (
                        <li key={d.deal_id}>
                          <a
                            href={dealUrl(d.account_id || d.deal_id, d.deal_id)}
                            className="flex items-baseline gap-2 text-text hover:underline"
                          >
                            <span className="text-sm font-medium">
                              {d.term_year ? `${d.term_year} term` : (d.app_number || 'Policy')}
                            </span>
                            {d.stage ? (
                              <Badge variant="outline" className="text-[9px]">
                                {d.stage}
                              </Badge>
                            ) : null}
                            {d.hull_value ? (
                              <span className="font-mono text-[10px] text-text-dim">
                                {formatCurrency(d.hull_value)}
                              </span>
                            ) : null}
                          </a>
                          {showName ? (
                            <div className="mt-0.5 text-[10px] text-text-dim">{d.account_name}</div>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}

              {/* Approved pilots */}
              {data.approved_pilots?.length > 0 ? (
                <div>
                  <h4 className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-text-dim">
                    Approved pilots
                  </h4>
                  <ul className="space-y-1">
                    {data.approved_pilots.map((p) => (
                      <li key={p.contact_id}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openPeek(openContactPeek(p.contact_id));
                          }}
                          className="text-sm text-text hover:underline"
                        >
                          {p.name || p.contact_id}
                        </button>
                        {p.interest ? (
                          <span className="ml-2 text-[10px] text-text-dim">
                            {p.interest}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </>
          ) : null}
        </div>

        {data?.crm_url ? (
          <div className="border-t border-border bg-surface px-6 py-3 flex justify-end">
            <a
              href={data.crm_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-text-muted hover:text-text"
            >
              <ExternalLink size={12} strokeWidth={1.5} />
              Open in CRM
            </a>
          </div>
        ) : null}
      </SheetContent>
      {data?.tail_number ? (
        <AircraftIdentityDialog
          aircraft={{ tail_number: data.tail_number }}
          open={editing}
          onOpenChange={(o) => { if (!o) setEditing(false); }}
          onSaved={() => setReload((n) => n + 1)}
        />
      ) : null}
    </Sheet>
  );
}
